import React, { useEffect, useState } from 'react';
import { Flex, useBreakpointValue, useDisclosure, useToast } from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import { Contract, SignatureModal } from 'ts/common/components';
import { ContractContext } from 'ts/common/contract/types';
import { useTranslate } from 'ts/common/hooks';
import ResponsiveButtonGroup from 'client_react/booking/components/ResponsiveButtonGroup';
import { isContractError } from 'client_react/common/hooks/useContract';
import { useClientContract } from 'client_react/contract/App';
import { ROUTE } from 'client_react/contract/common';
import { useContractResponsiveButtons } from 'client_react/contract/hooks/useContractResponsiveButtons';
import ViewInvoiceModal from './components/ViewInvoiceModal';

const ContractViewPage: React.FC = () => {
    const t = useTranslate('contract.viewPage');
    const navigate = useNavigate();
    const isMobile = useBreakpointValue({ base: true, md: false });
    const { contract, contractUserEmail, updateContract, signContract } = useClientContract();

    const {
        isOpen: isSignatureModalOpen,
        onOpen: openSignatureModal,
        onClose: closeSignatureModal
    } = useDisclosure();

    const {
        isOpen: isViewInvoiceModalOpen,
        onOpen: openViewInvoiceModal,
        onClose: closeViewInvoiceModal
    } = useDisclosure();

    const [signing, setSigning] = useState(false);

    const { fillableFieldData, setFillableFieldData, handleContractStart, getButtons } =
        useContractResponsiveButtons({
            t,
            onBack: () => navigate(`../${ROUTE.EMAIL}`),
            onSign: openSignatureModal,
            context: ContractContext.CLIENT_STANDALONE_CONTRACT,
            contract: contract || undefined,
            contractUserEmail: contractUserEmail || undefined
        });

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    useEffect(() => {
        if (!contract?.bodyHtml) {
            navigate(`../${ROUTE.EMAIL}`);
        }
    }, [contract, navigate]);

    const errorToast = useToast({
        title: t('apiError'),
        status: 'error'
    });

    const handleUpdateContract = async () => {
        return updateContract({
            data: {
                id: contract?.publicId,
                fillableFieldData: fillableFieldData,
                type: 'contract'
            },
            search: {
                email: contractUserEmail ?? ''
            }
        });
    };

    const handleSign = async (
        currentContract: SpApi.Client.IContract,
        signaturePaths: string,
        svgViewbox: string
    ) => {
        return signContract({
            data: {
                email: contractUserEmail,
                signaturePaths: signaturePaths,
                svgViewbox: svgViewbox,
                state: currentContract.state,
                type: 'contract-signature'
            }
        });
    };

    const onAcceptAndSign = async (signaturePaths: string, svgViewbox: string) => {
        if (signing) {
            return;
        }

        setSigning(true);

        try {
            const currentContract =
                fillableFieldData.length > 0 ? await handleUpdateContract() : contract;

            if (!currentContract || isContractError(currentContract)) {
                errorToast();
                setSigning(false);
                return;
            }

            const contractSignatureResponse = await handleSign(
                currentContract,
                signaturePaths,
                svgViewbox
            );

            if (!contractSignatureResponse || isContractError(contractSignatureResponse)) {
                errorToast();
                setSigning(false);
                return;
            }

            if (contractSignatureResponse.invoices[0]) {
                openViewInvoiceModal();
            }
        } finally {
            setSigning(false);
        }
    };

    if (!contract?.bodyHtml || !contractUserEmail) {
        return null;
    }

    return (
        <>
            <Contract
                brandName={contract?.brandName}
                contractData={contract}
                contractUserEmail={contractUserEmail}
                openSignatureModal={openSignatureModal}
                onFillableFieldsChange={setFillableFieldData}
                onStart={handleContractStart}
                context={ContractContext.CLIENT_STANDALONE_CONTRACT}
            />
            {isMobile && (
                <Flex justifyContent="center" mt={8}>
                    <ResponsiveButtonGroup buttons={getButtons()} />
                </Flex>
            )}
            <SignatureModal
                isOpen={isSignatureModalOpen}
                onClose={closeSignatureModal}
                onAcceptAndSign={(signaturePaths, svgViewbox) => {
                    onAcceptAndSign(signaturePaths, svgViewbox);
                    closeSignatureModal();
                }}
                context={ContractContext.CLIENT_STANDALONE_CONTRACT}
            />
            <ViewInvoiceModal
                isOpen={isViewInvoiceModalOpen}
                onClose={closeViewInvoiceModal}
                onViewInvoice={() => {
                    window.open(contract?.invoices[0].links.portal.href, '_blank');
                    closeViewInvoiceModal();
                }}
            />
        </>
    );
};

export default ContractViewPage;
